import React, { Dispatch, PropsWithChildren, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';

import {
    AgentToolKits,
    PaperAgentGetToolKitsInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import Cookies from 'js-cookie';
import { isNil } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { AppContext } from 'src/contexts/appContext';

const AGENT_ENDPOINT = 'agentEndpoint';
const AGENT_TOOLS = 'agentTools';

interface IAgentContext {
    selectedToolkits: string[] | undefined;
    setSelectedToolkits: Dispatch<SetStateAction<string[] | undefined>>;
    availableToolkits: AgentToolKits;
    endpoint: string;
    setEndpoint: Dispatch<SetStateAction<string>>;
    loading: boolean;
}

export const defaultAgentContext: IAgentContext = {
    selectedToolkits: undefined,
    setSelectedToolkits: () => {},
    availableToolkits: [],
    endpoint: 'LIVE',
    setEndpoint: () => {},
    loading: false
};

export const AgentContext = React.createContext(defaultAgentContext);

export const AgentContextProvider: React.FC<PropsWithChildren> = (props) => {
    const children = props.children;

    const { userInfo, setTopAlertMsg } = useContext(AppContext);

    const [loading, setLoading] = useState(false);
    const [selectedToolkits, setSelectedToolkits] = useState<string[] | undefined>(
        JSON.parse(Cookies.get(AGENT_TOOLS) ?? 'null') ?? undefined
    );
    const [availableToolkits, setAvailableToolkits] = useState<AgentToolKits>();
    const [endpoint, _setEndpoint] = useState(Cookies.get(AGENT_ENDPOINT) ?? 'LIVE');

    function sanitizeEndpoint(value: string) {
        return value.toUpperCase().replace(/[^A-Z0-9_]/g, '');
    }

    const setEndpoint: Dispatch<SetStateAction<string>> = (arg) => {
        if (typeof arg === 'function') {
            _setEndpoint((prev) => sanitizeEndpoint(arg(prev)));
        } else {
            _setEndpoint(sanitizeEndpoint(arg));
        }
    };

    const value: IAgentContext = useMemo(
        () => ({
            selectedToolkits,
            setSelectedToolkits,
            endpoint,
            setEndpoint,
            loading: loading,
            availableToolkits: availableToolkits ?? []
        }),
        [availableToolkits, loading, selectedToolkits, setSelectedToolkits, endpoint, setEndpoint]
    );

    useEffect(() => {
        if (!isNil(userInfo.username)) {
            setLoading(true);

            contractConfigurationClient
                .paperAgentGetToolKits(
                    PaperAgentGetToolKitsInput.clone({
                        user: userInfo.username
                    })
                )
                .subscribe({
                    next: (response) => {
                        setAvailableToolkits(response.toolkits);
                        setLoading(false);
                    },
                    error: (err) => {
                        setTopAlertMsg(JSON.stringify(err));
                        setLoading(false);
                    }
                });
        }
    }, [userInfo?.username]);

    useEffect(() => {
        if (endpoint.length === 0) {
            Cookies.remove(AGENT_ENDPOINT);
        } else {
            Cookies.set(AGENT_ENDPOINT, endpoint);
        }
    }, [endpoint]);

    useEffect(() => {
        if (isNil(selectedToolkits)) {
            Cookies.remove(AGENT_TOOLS);
        } else {
            Cookies.set(AGENT_TOOLS, JSON.stringify(selectedToolkits));
        }
    }, [selectedToolkits]);

    return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
};
